import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  NEGATIVE_COLOR,
  LINE_COLOR,
  POSITIVE_COLOR,
} from "../../charts/chartOptions";
import {
  WIDGET_CATEGORIES,
  WIDGET_DEFINITIONS,
  WIDGET_TYPES,
  type WidgetCategory,
  type WidgetType,
} from "./types";

export type AddWidgetSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingTypes: ReadonlySet<WidgetType>;
  onSelect: (type: WidgetType) => void;
};

export function AddWidgetSheet({
  open,
  onOpenChange,
  existingTypes,
  onSelect,
}: AddWidgetSheetProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="fixed inset-y-0 right-0 left-auto top-0 bottom-0 grid h-full max-h-screen w-full max-w-md translate-x-0 translate-y-0 grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-none border-l p-0 sm:rounded-none"
      >
        <DialogHeader className="border-b border-outline-variant px-6 py-4">
          <DialogTitle>Add widget</DialogTitle>
          <DialogDescription>
            Pick a widget to drop into your dashboard. Already-added widgets
            are disabled.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-4 py-4">
          {WIDGET_CATEGORIES.map((category) => (
            <CategoryGroup
              key={category}
              category={category}
              existingTypes={existingTypes}
              onSelect={onSelect}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryGroup({
  category,
  existingTypes,
  onSelect,
}: {
  category: WidgetCategory;
  existingTypes: ReadonlySet<WidgetType>;
  onSelect: (type: WidgetType) => void;
}) {
  const types = WIDGET_TYPES.filter(
    (t) => WIDGET_DEFINITIONS[t].category === category,
  );
  if (types.length === 0) return null;
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant">
        {category}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {types.map((type) => {
          const def = WIDGET_DEFINITIONS[type];
          const added = existingTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              disabled={added}
              onClick={() => {
                onSelect(type);
              }}
              className="group relative flex flex-col gap-2 rounded-md border border-outline-variant bg-surface-container p-2 text-left transition-colors hover:border-primary hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-outline-variant disabled:hover:bg-surface-container"
              aria-label={`Add ${def.title} widget`}
            >
              <WidgetPreview type={type} />
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-on-surface">
                  {def.title}
                </span>
                {added && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-on-surface-muted">
                    Added
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const PREVIEW_W = 160;
const PREVIEW_H = 72;

function PreviewFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex h-[72px] w-full items-center justify-center overflow-hidden rounded border border-outline-variant bg-surface"
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

function WidgetPreview({ type }: { type: WidgetType }) {
  switch (type) {
    case "kpi_total_pnl":
      return (
        <PreviewFrame>
          <div className="flex flex-col items-center">
            <span
              className="font-display text-2xl font-medium tracking-tight"
              style={{ color: POSITIVE_COLOR }}
            >
              $1.2k
            </span>
            <span className="text-[8px] font-medium uppercase tracking-wide text-on-surface-variant">
              Total PnL
            </span>
          </div>
        </PreviewFrame>
      );
    case "kpi_win_rate":
      return (
        <PreviewFrame>
          <div className="flex flex-col items-center">
            <span className="font-display text-2xl font-medium tracking-tight text-on-surface">
              68%
            </span>
            <span className="text-[8px] font-medium uppercase tracking-wide text-on-surface-variant">
              Win rate
            </span>
          </div>
        </PreviewFrame>
      );
    case "kpi_profit_factor":
      return (
        <PreviewFrame>
          <div className="flex flex-col items-center">
            <span className="font-display text-2xl font-medium tracking-tight text-on-surface">
              2.41
            </span>
            <span className="text-[8px] font-medium uppercase tracking-wide text-on-surface-variant">
              Profit factor
            </span>
          </div>
        </PreviewFrame>
      );
    case "kpi_avg_pnl":
      return (
        <PreviewFrame>
          <div className="flex flex-col items-center">
            <span
              className="font-display text-2xl font-medium tracking-tight"
              style={{ color: POSITIVE_COLOR }}
            >
              $48
            </span>
            <span className="text-[8px] font-medium uppercase tracking-wide text-on-surface-variant">
              Avg P&amp;L
            </span>
          </div>
        </PreviewFrame>
      );
    case "chart_wins_losses":
      return (
        <PreviewFrame>
          <svg width={PREVIEW_W} height={PREVIEW_H} viewBox={`0 0 ${PREVIEW_W} ${PREVIEW_H}`}>
            {[12, 32, 52, 72, 92, 112, 132].map((x, i) => {
              const winH = [22, 38, 14, 44, 28, 40, 18][i]!;
              const lossH = [10, 8, 28, 6, 16, 4, 22][i]!;
              return (
                <g key={x}>
                  <rect
                    x={x}
                    y={PREVIEW_H - 8 - winH}
                    width={10}
                    height={winH}
                    fill={POSITIVE_COLOR}
                  />
                  <rect
                    x={x}
                    y={PREVIEW_H - 8}
                    width={10}
                    height={lossH}
                    fill={NEGATIVE_COLOR}
                  />
                </g>
              );
            })}
          </svg>
        </PreviewFrame>
      );
    case "chart_equity_curve":
      return (
        <PreviewFrame>
          <svg width={PREVIEW_W} height={PREVIEW_H} viewBox={`0 0 ${PREVIEW_W} ${PREVIEW_H}`}>
            <polyline
              fill="none"
              stroke={LINE_COLOR}
              strokeWidth={2}
              points="6,56 30,46 56,52 80,32 104,28 128,18 154,14"
            />
          </svg>
        </PreviewFrame>
      );
    case "chart_daily_pnl":
      return (
        <PreviewFrame>
          <svg width={PREVIEW_W} height={PREVIEW_H} viewBox={`0 0 ${PREVIEW_W} ${PREVIEW_H}`}>
            {[
              { x: 8, h: 22, up: true },
              { x: 24, h: 14, up: false },
              { x: 40, h: 32, up: true },
              { x: 56, h: 8, up: false },
              { x: 72, h: 28, up: true },
              { x: 88, h: 18, up: false },
              { x: 104, h: 36, up: true },
              { x: 120, h: 12, up: true },
              { x: 136, h: 20, up: false },
            ].map(({ x, h, up }) => (
              <rect
                key={x}
                x={x}
                y={up ? 36 - h : 36}
                width={12}
                height={h}
                fill={up ? POSITIVE_COLOR : NEGATIVE_COLOR}
              />
            ))}
            <line x1={0} y1={36} x2={PREVIEW_W} y2={36} stroke="currentColor" strokeOpacity={0.15} />
          </svg>
        </PreviewFrame>
      );
    case "chart_drawdown":
      return (
        <PreviewFrame>
          <svg width={PREVIEW_W} height={PREVIEW_H} viewBox={`0 0 ${PREVIEW_W} ${PREVIEW_H}`}>
            <polyline
              fill="none"
              stroke={NEGATIVE_COLOR}
              strokeWidth={2}
              points="6,12 30,18 56,42 80,58 104,46 128,30 154,20"
            />
            <line x1={0} y1={12} x2={PREVIEW_W} y2={12} stroke="currentColor" strokeOpacity={0.15} strokeDasharray="2 2" />
          </svg>
        </PreviewFrame>
      );
    case "panel_trades":
      return (
        <PreviewFrame>
          <div className="w-[88%] space-y-1">
            <div className="flex gap-1">
              {[34, 18, 24].map((w, i) => (
                <div
                  key={i}
                  className="h-2 rounded-sm bg-on-surface-variant/40"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex gap-1">
                {[34, 18, 24].map((w, i) => (
                  <div
                    key={i}
                    className="h-1.5 rounded-sm bg-on-surface-variant/20"
                    style={{ width: `${w}%` }}
                  />
                ))}
              </div>
            ))}
          </div>
        </PreviewFrame>
      );
    case "panel_calendar":
      return (
        <PreviewFrame>
          <div className="grid grid-cols-7 gap-[2px] p-1">
            {Array.from({ length: 21 }).map((_, i) => {
              const tints = ["", "bg-success-soft", "", "bg-danger-soft", "", "bg-success-soft", ""];
              const cls = tints[i % tints.length] || "";
              return (
                <div
                  key={i}
                  className={`h-3 w-3 rounded-[2px] border border-outline-variant ${cls}`}
                />
              );
            })}
          </div>
        </PreviewFrame>
      );
    case "panel_streak":
      return (
        <PreviewFrame>
          <div className="flex h-[44px] items-end gap-1">
            {[60, 80, 40, 90, 55, 70, 35, 85, 50, 95].map((h, i) => (
              <span
                key={i}
                className="w-1.5 rounded-[2px]"
                style={{
                  height: `${h}%`,
                  backgroundColor: i % 3 === 1 ? NEGATIVE_COLOR : POSITIVE_COLOR,
                }}
              />
            ))}
          </div>
        </PreviewFrame>
      );
  }
}
